import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import { setCustomSkillLevelIdMap } from '../../../next/CustomDash/filters/utils/customSkillTaxonomyRegistry';
import {
  buildSkillLevelIdMapFromTaxonomyLevels,
  getCachedCustomSkillNodesFromNested,
  getOrBuildNestedSkillReferenceTree,
  SKILL_REFERENCE_TAXONOMY_TREE_LEVELS,
} from '../../../next/CustomDash/filters/utils/skillReferenceTaxonomyTree';
import type { CustomSkillTreeNode } from '../../../next/CustomDash/filters/utils/skillTaxonomyTree';
import { useSkillTaxonomy } from '../../user-preferences/hooks/useSkillTaxonomySelection';
import { useCurrentReferenceTaxonomyVersion } from './useReferenceTaxonomyVersions';
import { useTaxonomyTree } from './useTaxonomyTree';

export type { CustomSkillTreeNode };

export const useCustomSkillTaxonomyTree = (
  overrideTaxonomyId?: number,
  taxonomyVersionOverride?: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const prefTaxonomyId = useSkillTaxonomy();
  const skillTaxonomyId = overrideTaxonomyId ?? prefTaxonomyId ?? 0;

  // Resolve a deterministic taxonomyVersion. Callers don't reliably carry one
  // (bare mounts, preference-synced or URL-restored SKILL_TAXONOMY values),
  // and an unversioned request is a different URQL cache entry than a
  // versioned one — the same tree got fetched twice per page depending on
  // render order. Default to the latest default taxonomy's version (the same
  // version createDefaultFilterMap stamps on SKILL_TAXONOMY) and hold the
  // fetch until versions are known.
  const { version: defaultSkillTaxonomyVersion, loading: isVersionLoading } =
    useCurrentReferenceTaxonomyVersion({
      taxonomyType: TaxonomyType.Skill,
      pause: !enabled,
    });
  const taxonomyVersion =
    taxonomyVersionOverride ?? defaultSkillTaxonomyVersion;

  const {
    treeData: rawSkillTaxonomyTreeNodes,
    levels: skillTaxonomyReferenceLevels,
    fetching: isSkillTaxonomyReferenceFetching,
    cacheKey: skillTaxonomyQueryCacheKey,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId: skillTaxonomyId,
    taxonomyVersion,
    levels: [...SKILL_REFERENCE_TAXONOMY_TREE_LEVELS],
    // Hold the request until the version is actually known. Gating on
    // isVersionLoading alone isn't enough: if the version lookup errors,
    // isVersionLoading flips false while taxonomyVersion stays undefined, and an
    // unversioned request is a different URQL cache entry than a versioned one —
    // exactly the cache split this hook exists to remove.
    pause: !enabled || isVersionLoading || !taxonomyVersion,
  });

  const levelIdMap = useMemo(() => {
    if (skillTaxonomyReferenceLevels.length === 0) return null;
    return buildSkillLevelIdMapFromTaxonomyLevels(skillTaxonomyReferenceLevels);
  }, [skillTaxonomyReferenceLevels]);

  const isCustomSkillLevelMapReady =
    levelIdMap !== null && Object.keys(levelIdMap).length > 0;

  if (isCustomSkillLevelMapReady) {
    setCustomSkillLevelIdMap(levelIdMap);
  }

  const skillTaxonomyReferenceNestedTree = useMemo(() => {
    if (rawSkillTaxonomyTreeNodes.length === 0) return null;
    return getOrBuildNestedSkillReferenceTree(
      skillTaxonomyQueryCacheKey,
      rawSkillTaxonomyTreeNodes
    );
  }, [rawSkillTaxonomyTreeNodes, skillTaxonomyQueryCacheKey]);

  const customSkillTree = useMemo(() => {
    if (isSkillTaxonomyReferenceFetching) return null;
    if (!skillTaxonomyReferenceNestedTree?.length) return null;
    const custom = getCachedCustomSkillNodesFromNested(
      skillTaxonomyReferenceNestedTree
    );
    return custom.length > 0 ? custom : null;
  }, [isSkillTaxonomyReferenceFetching, skillTaxonomyReferenceNestedTree]);

  return {
    customSkillTree,
    isCustomSkillLevelMapReady,
    // While the version lookup is in flight the tree query is paused (not
    // "fetching") — consumers gating on this flag must still treat it as
    // pending or they'd briefly see an empty, settled tree.
    isCustomSkillTaxonomyPending:
      enabled && (isVersionLoading || isSkillTaxonomyReferenceFetching),
    skillTaxonomyReferenceNestedTree,
    // Raw tree + level metadata for consumers that build their own
    // per-level selection lists (e.g. the report builder's custom-skill
    // breadcrumb picker via treeToCustomSkillSelectionLists).
    treeData: rawSkillTaxonomyTreeNodes,
    levels: skillTaxonomyReferenceLevels,
  };
};
